import { cn } from "@/lib/utils";
import { SCORE_HEX, SCORE_THRESHOLDS } from "./score-colors";

export type ScoreFilter = "high" | "medium" | "low" | "none";

const LEGEND_ITEMS: { key: ScoreFilter; label: string; description: string; color: string }[] = [
  {
    key: "high",
    label: "High",
    description: `Score ≥ ${SCORE_THRESHOLDS.high}`,
    color: SCORE_HEX.high,
  },
  {
    key: "medium",
    label: "Medium",
    description: `Score ${SCORE_THRESHOLDS.medium}–<${SCORE_THRESHOLDS.high}`,
    color: SCORE_HEX.medium,
  },
  {
    key: "low",
    label: "Low",
    description: `Score < ${SCORE_THRESHOLDS.medium}`,
    color: SCORE_HEX.low,
  },
  {
    key: "none",
    label: "Unscored",
    description: "No score data",
    color: SCORE_HEX.none,
  },
];

interface MapLegendProps {
  activeFilters: Set<ScoreFilter>;
  onToggle: (filter: ScoreFilter) => void;
}

export function MapLegend({ activeFilters, onToggle }: MapLegendProps) {
  const hasFilters = activeFilters.size > 0;

  return (
    <div className="absolute top-3 left-3 z-10 rounded-lg border border-border bg-card/90 backdrop-blur-sm p-3 shadow-lg">
      <div className="mb-2">
        <p className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-muted-foreground">
          FloScore
        </p>
        <p className="text-[10px] text-muted-foreground/70">Click to filter</p>
      </div>
      <div className="flex flex-col gap-1.5">
        {LEGEND_ITEMS.map((item) => {
          const isActive = !hasFilters || activeFilters.has(item.key);
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => onToggle(item.key)}
              className={cn(
                "flex items-center gap-2 rounded px-2 py-1 text-left transition-opacity hover:bg-muted/50",
                isActive ? "opacity-100" : "opacity-40",
              )}
            >
              <span
                className="size-3 shrink-0 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              <span className="text-xs font-medium">{item.label}</span>
              <span className="font-mono text-[10px] tabular-nums text-muted-foreground">
                {item.description}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
